import { getAccessToken } from './auth';

class WebSocketService {
    constructor() {
        this.socket = null;
        this.reconnectAttempts = 0;
        this.maxReconnectAttempts = 5;
        this.reconnectInterval = 3000;
        this.isConnecting = false;
        this.eventHandlers = new Map();
        this.baseUrl = process.env.REACT_APP_WS_URL || 'ws://localhost:8080';
    }

    /**
     * Connect to WebSocket server
     * @param {string} patientId - Patient ID for authentication
     */
    async connect(patientId) {
        console.log('WebSocket already connected');
        if (this.socket && this.socket.readyState === WebSocket.OPEN) {
            console.log('WebSocket already connected');
            return;
        }

        this.isConnecting = true;

        try {
            const token = await getAccessToken();
            // const wsUrl = `${this.baseUrl}`;
            const wsUrl = process.env.REACT_APP_WS_URL;

            console.log('WebSocket Connection Debug:');
            console.log('- baseUrl:', this.baseUrl);
            console.log('- patientId:', patientId);
            console.log('- token:', token ? 'present' : 'missing');
            console.log('- Full URL:', wsUrl);

            this.socket = new WebSocket(wsUrl);

            this.socket.onopen = this.handleOpen.bind(this);
            this.socket.onmessage = this.handleMessage.bind(this);
            this.socket.onclose = this.handleClose.bind(this);
            this.socket.onerror = this.handleError.bind(this);

        } catch (error) {
            console.error('Failed to get auth token for WebSocket:', error);
            this.isConnecting = false;
            this.handleReconnect();
        }
    }

    /**
     * Handle WebSocket connection open
     */
    handleOpen() {
        console.log('WebSocket connected successfully');
        this.isConnecting = false;
        this.reconnectAttempts = 0;

        // Emit connection event
        this.emit('connection', { status: 'connected' });
    }

    /**
     * Handle incoming WebSocket messages
     * @param {MessageEvent} event - WebSocket message event
     */
    handleMessage(event) {
        try {
            const data = JSON.parse(event.data);
            console.log('WebSocket message received:', data);

            // Emit the specific event type (support both 'event' and 'type' properties)
            const eventType = data.type || data.event;
            if (eventType) {
                this.emit(eventType, data.payload || data);
            }

            // Emit generic message event
            this.emit('message', data);

        } catch (error) {
            console.error('Error parsing WebSocket message:', error);
            this.emit('error', { message: 'Failed to parse message' });
        }
    }

    /**
     * Handle WebSocket connection close
     * @param {CloseEvent} event - Close event
     */
    handleClose(event) {
        console.log('WebSocket connection closed:', event.code, event.reason);
        this.isConnecting = false;

        this.emit('connection', {
            status: 'disconnected',
            code: event.code,
            reason: event.reason
        });

        // Attempt to reconnect if not a normal closure
        if (event.code !== 1000) {
            this.handleReconnect();
        }
    }

    /**
     * Handle WebSocket errors
     * @param {Event} event - Error event
     */
    handleError(event) {
        console.error('WebSocket error:', event);
        this.isConnecting = false;
        this.emit('error', { message: 'WebSocket connection error' });
    }

    /**
     * Handle reconnection logic
     */
    handleReconnect() {
        if (this.reconnectAttempts >= this.maxReconnectAttempts) {
            console.error('Max reconnection attempts reached');
            this.emit('error', { message: 'Max reconnection attempts reached' });
            return;
        }

        this.reconnectAttempts++;
        console.log(`Attempting to reconnect (${this.reconnectAttempts}/${this.maxReconnectAttempts})...`);

        setTimeout(() => {
            this.isConnecting = false;
            // Note: We'll need patientId to reconnect
            // This should be handled by the calling component
            this.emit('reconnect', { attempt: this.reconnectAttempts });
        }, this.reconnectInterval);
    }

    /**
     * Send message through WebSocket
     * @param {string} type - Message type
     * @param {Object} payload - Message payload
     */
    send(type, payload = {}) {
        if (this.socket && this.socket.readyState === WebSocket.OPEN) {
            const message = {
                type,
                payload,
                timestamp: new Date().toISOString()
            };

            this.socket.send(JSON.stringify(message));
            console.log('WebSocket message sent:', message);
        } else {
            console.warn('WebSocket not connected. Cannot send message.');
        }
    }

    /**
     * Subscribe to specific event types
     * @param {string} eventType - Event type to listen for
     * @param {Function} handler - Event handler function
     */
    on(eventType, handler) {
        if (!this.eventHandlers.has(eventType)) {
            this.eventHandlers.set(eventType, []);
        }
        this.eventHandlers.get(eventType).push(handler);
    }

    /**
     * Unsubscribe from specific event types
     * @param {string} eventType - Event type to stop listening for
     * @param {Function} handler - Event handler function to remove
     */
    off(eventType, handler) {
        if (this.eventHandlers.has(eventType)) {
            const handlers = this.eventHandlers.get(eventType);
            const index = handlers.indexOf(handler);
            if (index > -1) {
                handlers.splice(index, 1);
            }
        }
    }

    /**
     * Emit event to all registered handlers
     * @param {string} eventType - Event type
     * @param {Object} data - Event data
     */
    emit(eventType, data) {
        if (this.eventHandlers.has(eventType)) {
            this.eventHandlers.get(eventType).forEach(handler => {
                try {
                    handler(data);
                } catch (error) {
                    console.error(`Error in event handler for ${eventType}:`, error);
                }
            });
        }
    }

    /**
     * Disconnect WebSocket
     */
    disconnect() {
        if (this.socket) {
            this.socket.close(1000, 'Client disconnecting');
            this.socket = null;
        }
        this.isConnecting = false;
        this.reconnectAttempts = 0;
    }

    /**
     * Get connection status
     * @returns {string} - Connection status
     */
    getStatus() {
        if (!this.socket) return 'disconnected';

        switch (this.socket.readyState) {
            case WebSocket.CONNECTING:
                return 'connecting';
            case WebSocket.OPEN:
                return 'connected';
            case WebSocket.CLOSING:
                return 'closing';
            case WebSocket.CLOSED:
                return 'disconnected';
            default:
                return 'unknown';
        }
    }

    /**
     * Check if WebSocket is connected
     * @returns {boolean} - True if connected
     */
    isConnected() {
        return this.socket && this.socket.readyState === WebSocket.OPEN;
    }
}

// Create singleton instance
const websocketService = new WebSocketService();

export default websocketService;
